/**
 * AI activity: a read-only, per-tenant trail of what the AI did, why, with which model and tools and which data
 * (flag `ai.activity_ui`, owner/admin only). Built from the append-only AI logs, approvals and Autonomy Ladder decisions.
 * It never carries prompts, model output text, message bodies or contact details: ids, codes and counts only.
 */
import { z } from 'zod';

export const AI_ACTIVITY_FLAG = 'ai.activity_ui';
export const ACTIVITY_KINDS = ['model_call', 'output', 'action', 'autonomy', 'guardrail'] as const;
export const ActivityKindSchema = z.enum(ACTIVITY_KINDS);
export type ActivityKind = z.infer<typeof ActivityKindSchema>;

export const ActivityQuerySchema = z.object({
  kind: ActivityKindSchema.optional(),
  /** ISO time: only entries older than this (paging) */
  before: z.string().datetime().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const ActivityToneSchema = z.enum(['ok', 'warn', 'error', 'neutral']);
export const ActivityDetailValue = z.union([z.string(), z.number(), z.boolean(), z.null(), z.array(z.string())]);

export const ActivityEntrySchema = z.object({
  id: z.string(),
  kind: ActivityKindSchema,
  at: z.string(),
  /** i18n key (`ai-activity.event.*`) for the one-line summary; `params` fill it */
  titleKey: z.string(),
  params: z.record(z.string(), z.union([z.string(), z.number()])),
  tone: ActivityToneSchema,
  /** provider/model when a model was involved */
  model: z.string().nullable(),
  costMicros: z.number().int().nonnegative().nullable(),
  /** who: a user id, `system:*`, or null */
  actor: z.string().nullable(),
  /** why / which tool / which data: codes, ids and counts only */
  details: z.record(z.string(), ActivityDetailValue),
});
export type ActivityEntry = z.infer<typeof ActivityEntrySchema>;

export const ActivityPageSchema = z.object({ entries: z.array(ActivityEntrySchema), nextBefore: z.string().nullable() });
export type ActivityPage = z.infer<typeof ActivityPageSchema>;

/** Audit-log actions shown as guardrail events, with the only metadata keys that may leave the server. */
export const GUARDRAIL_ACTIONS: Record<string, readonly string[]> = {
  'ai.session.injection_blocked': ['signals', 'blockedTools', 'outputViolations'],
  'approvals.execution.denied_permission': ['tool', 'principal', 'missing', 'reason'],
  'approvals.execution.refused': ['reason'],
  'ai.budget.cap_reached': ['scope', 'period'],
  'ai.confidence.fallback': ['purpose', 'score', 'level', 'reasons'],
};
